/**
 * StaffProvisioningQueue — Liste aller provisioning_jobs im Status
 * 'needs_attention'. Pro Job:
 *   - alle Konfig-Daten anzeigen (Mailbox-Adresse, Display-Name etc.)
 *   - Deep-Link zum passenden Admin-Tool (Stalwart Webadmin fuer Mail)
 *   - "Erledigt"-Button schickt POST /api/staff/provisioning/:id/complete
 *     mit linked_name (= mailbox_address) und triggert Welcome-Mail
 *
 * TQ #9696 (Staff-Migration Teil C, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffProvisioningQueue.tsx — direkter
 * fetch gegen portal-api /api/staff/provisioning/* (keycloak-Token aus
 * useAuth), useToast auf org-portal-Konvention (../../components/ui →
 * showToast(type, msg)) umgestellt.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../../components/ui'
import { apiPrefix } from '../../lib/runtimeConfig'


interface NeedsAttentionRow {
  job_id: number
  subscription_id: number
  service_type: string
  config: Record<string, unknown>
  last_error: string
  job_created_at: string
  erpnext_subscription_id: string
  erpnext_customer_id: string
  plan_id: string
  plan_name: string | null
  rate: number
  customer_email: string | null
  customer_name: string
}

const SERVICE_LABEL: Record<string, string> = {
  'personal-mail': 'Personal Mailbox',
  'workspace-mail': 'Workspace Mailbox',
}

const STALWART_ADMIN_URL = 'https://mail.it-sce.de/admin/'

export default function StaffProvisioningQueue() {
  const { keycloak } = useAuth()
  const { showToast } = useToast()
  const toastError = useCallback((msg: string) => showToast('error', msg), [showToast])
  const toastSuccess = useCallback((msg: string) => showToast('success', msg), [showToast])
  const [rows, setRows] = useState<NeedsAttentionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [completing, setCompleting] = useState<number | null>(null)

  const load = useCallback(async () => {
    if (!keycloak?.token) return
    setLoading(true)
    try {
      const res = await fetch(`${apiPrefix()}/staff/provisioning/needs-attention`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const d = await res.json()
      setRows(d.jobs || [])
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Laden fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }, [keycloak?.token, toastError])

  useEffect(() => {
    load()
    const t = setInterval(load, 30_000)
    return () => clearInterval(t)
  }, [load])

  const proposedLinkedName = (row: NeedsAttentionRow): string => {
    const local = row.config.mailbox_local as string | undefined
    const domain = (row.config.mailbox_domain as string | undefined) || 'scemail.de'
    if (local) return `${local}@${domain}`
    return ''
  }

  const handleComplete = async (row: NeedsAttentionRow) => {
    if (!keycloak?.token) return
    const proposed = proposedLinkedName(row)
    const linkedName = window.prompt(
      'Wurde der Account in Stalwart angelegt? Bitte Mailbox-Adresse zur Bestätigung eintragen:',
      proposed,
    )
    if (!linkedName) return
    setCompleting(row.job_id)
    try {
      const res = await fetch(
        `${apiPrefix()}/staff/provisioning/${row.job_id}/complete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            linked_name: linkedName,
            linked_type: row.service_type.endsWith('-mail') ? 'mailbox' : '',
          }),
        },
      )
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new Error(errData.error || `HTTP ${res.status}`)
      }
      const d = await res.json()
      if (d.warning) {
        toastError(`Erledigt, aber: ${d.warning}`)
      } else {
        toastSuccess(`Job ${row.job_id} abgeschlossen — Welcome-Mail an ${row.customer_email || '-'}`)
      }
      await load()
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Abschluss fehlgeschlagen')
    } finally {
      setCompleting(null)
    }
  }

  if (loading && rows.length === 0) {
    return (
      <div className="page-content">
        <div className="page-header">
          <h1>⚙️ Provisioning Queue</h1>
        </div>
        <p style={{ color: '#6b7280' }}>Lade …</p>
      </div>
    )
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>⚙️ Provisioning Queue</h1>
        <p className="page-subtitle">
          Subscriptions die manuell in den Backend-Systemen eingerichtet werden müssen.
          Auto-Refresh alle 30s.
        </p>
      </div>

      {rows.length === 0 ? (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            background: '#f9fafb',
            border: '1px dashed #d1d5db',
            borderRadius: 12,
            color: '#6b7280',
          }}
        >
          ✓ Keine offenen Provisioning-Jobs.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map(row => (
            <div
              key={row.job_id}
              style={{
                background: 'white',
                border: '1px solid #fde68a',
                borderRadius: 12,
                padding: '1.25rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.5rem',
                  gap: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>
                    {SERVICE_LABEL[row.service_type] || row.service_type}
                  </h3>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Job #{row.job_id} · {row.plan_name || row.plan_id} ·{' '}
                    {row.rate.toFixed(2)} €/Mo · seit{' '}
                    {new Date(row.job_created_at).toLocaleString('de-DE')}
                  </div>
                </div>
                <span
                  style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '0.25rem 0.7rem',
                    borderRadius: 999,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  needs_attention
                </span>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '0.75rem',
                  marginTop: '1rem',
                  fontSize: '0.9rem',
                }}
              >
                <Field label="Kunde">
                  <div>{row.customer_name || row.customer_email || '-'}</div>
                  {row.customer_email && (
                    <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{row.customer_email}</div>
                  )}
                </Field>
                <Field label="ERPNext-Kunde">
                  <code>{row.erpnext_customer_id}</code>
                </Field>
                <Field label="ERPNext-Subscription">
                  <code>{row.erpnext_subscription_id}</code>
                </Field>
                <Field label="Anzeigename">
                  {(row.config.display_name as string) || '—'}
                </Field>
                {row.service_type.endsWith('-mail') && (
                  <>
                    <Field label="Wunsch-Mailbox">
                      <code>{proposedLinkedName(row)}</code>
                    </Field>
                  </>
                )}
              </div>

              {row.last_error && (
                <div
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem 0.7rem',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 6,
                    fontSize: '0.78rem',
                    color: '#78350f',
                  }}
                >
                  Status: {row.last_error}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: '0.6rem',
                  marginTop: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                {row.service_type.endsWith('-mail') && (
                  <a
                    href={STALWART_ADMIN_URL}
                    target="_blank"
                    rel="noopener"
                    className="btn"
                  >
                    Stalwart Webadmin öffnen ↗
                  </a>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => handleComplete(row)}
                  disabled={completing === row.job_id}
                >
                  {completing === row.job_id
                    ? 'wird abgeschlossen …'
                    : '✓ Erledigt — Welcome-Mail senden'}
                </button>
              </div>

              <details style={{ marginTop: '0.75rem', fontSize: '0.78rem' }}>
                <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                  Vollständige Konfiguration anzeigen
                </summary>
                <pre
                  style={{
                    background: '#f9fafb',
                    padding: '0.5rem',
                    borderRadius: 6,
                    marginTop: '0.4rem',
                    overflowX: 'auto',
                    fontSize: '0.75rem',
                  }}
                >
                  {JSON.stringify(row.config, null, 2)}
                </pre>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: 0.05,
          color: '#6b7280',
          marginBottom: '0.2rem',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  )
}
